import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { getActiveActivityProfiles } from "PFCore/helpers/get_active_activity_profiles";
import { getProfileName, isProfileActive } from "PFCore/helpers/profile";
import { useOwnerDelete } from "PFCore/hooks/queries/owners";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { Activity, Owner, Profile } from "PFTypes";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

type UseOwnerRemoveReturn = {
  handleRemove: (profile: Owner) => void;
  getRemoveModalContent: (profile: Profile) => string | undefined;
};

export const useOwnerRemove = (
  activity: Activity,
  onRemoveSelf: () => void = () => {}
): UseOwnerRemoveReturn => {
  const { data: currentProfile } = useCurrentProfile();
  const { id: currentProfileId } = currentProfile;

  const { showGrowl } = useGrowlStoreActions();
  const { t } = useTranslation("activities");
  const { mutate: deleteOwner } = useOwnerDelete(activity.id);

  const taskOwners = getActiveActivityProfiles(activity.owners);

  const handleRemove = (owner: Owner) => {
    const isMe = owner.profile.id === currentProfileId;

    owner.id &&
      deleteOwner(Number(owner.id), {
        onSuccess: () => {
          isMe && onRemoveSelf();
          showGrowl({
            message: isMe
              ? t("show.parts.successRemoveOwner")
              : t("show.parts.profileRemovedCoOwner", { profileName: getProfileName(owner.profile) }),
            kind: "success"
          });
        },
        onError: () =>
          showGrowl({
            message: isMe ? t("show.parts.issueRemovingOwnership") : t("show.parts.unknownError"),
            kind: "error"
          })
      });
  };

  const getRemoveModalContent = useCallback(
    (profile: Profile) => {
      if (!isProfileActive(profile)) {
        return;
      }
      const activeOwners = taskOwners.filter(({ profile }) => isProfileActive(profile));

      if (activeOwners.length <= 1 && profile.id === currentProfileId) {
        return t("show.parts.removingYourselfAsOwnerNotAllowed");
      }
      if (activeOwners.length <= 1) {
        return t("show.parts.removingLastOwner");
      }
      if (profile.id === currentProfileId) {
        return t("show.parts.removingYourselfAsOwner");
      }
    },
    [currentProfileId, taskOwners, t]
  );

  return { handleRemove, getRemoveModalContent };
};
